import { Collapse, Group, Stack as MantineStack, Stack, Switch } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router';
import { useEpics, useProcessTaskTypesWithOptions } from '../../../api/process-client/process-client.hooks';
import type { WorkPeriod } from '../../../api/work-periods-client/work-periods-client.type';
import { useGetWorkflows } from '../../../api/workflows-client/workflows.client.hooks';
import { FilterPanel } from '../../../components/filters/filter-panel';
import type { FilterGroup } from '../../../components/filters/filter-panel.type';
import { useDeepCompareEffect } from '../../../hooks/useDeepCompareEffect';
import { useGlobalStore } from '../../../store/global-store/global-store';
import { useProcessStore } from '../../../store/process-store/process-store.hooks';
import { secondaryBase } from '../../../styles/design-tokens';
import { Divider } from '../../../ui-library/divider/divider';
import { Icon } from '../../../ui-library/icon/icon';
import { Select } from '../../../ui-library/select/select';
import { Text } from '../../../ui-library/typography/typography';
import { TaskType, type TaskTypeValue } from '../../tasks-table/tasks-table.type';
import { useWorkPeriodsData } from '../../work-periods/hooks/work-periods.hooks';
import { ProcessViewMode } from '../process.type';
import {
  buildTaskTypeLabelMap,
  createProcessFilterGroups,
  createResetFiltersHandler,
  createWorkflowSelectHandler,
} from './process-filter-helpers';
import { TaskSelect } from './task-select';

type Props = {
  leftTaskTypes: string[];
  rightTaskTypes: string[];
};

export function ProcessFilterPanel({ leftTaskTypes, rightTaskTypes }: Props) {
  const { t, i18n } = useTranslation();
  const organizationId = useGlobalStore((state) => state.organization)?.id || '';
  const portfolioId = useGlobalStore((state) => state.portfolio)?.id || null;
  const { scope, setScope, isComparisonView, setIsComparisonView, rightScope, setRightScope } = useProcessStore();

  // Destructure values from scope for easier use
  const { taskTypes, epic, portfolio, team, board, useWorkflow, workflow, startDate, endDate, workPeriod } = scope;

  // Refs to track all seen task types for both sides
  const leftAllSeenTaskTypesRef = useRef<Set<string>>(new Set(Object.values(TaskType)));
  const rightAllSeenTaskTypesRef = useRef<Set<string>>(new Set(Object.values(TaskType)));
  const [leftForceRender, setLeftForceRender] = useState(0);
  const [rightForceRender, setRightForceRender] = useState(0);

  // Track whether the dedicated task-types endpoint has returned usable data.
  // When true, the process-graph hydration path is skipped in favour of the
  // endpoint results so the two sources don't fight each other.
  const leftTypesFromEndpointRef = useRef(false);
  const rightTypesFromEndpointRef = useRef(false);

  // Fetch distinct task types in parallel with the graph — no date filtering,
  // so this returns quickly and populates the dropdown before the graph arrives.
  // The labeled options ride along on the same single HTTP call; the string
  // list keeps feeding the seen-types set machinery unchanged.
  const scopeEnabled = Boolean(team?.id || workflow?.id);
  const { taskTypes: leftEndpointTaskTypes, taskTypeOptions: leftEndpointTaskTypeOptions } =
    useProcessTaskTypesWithOptions(
      { teamId: team?.id, workflowId: workflow?.id, boardId: board?.id, workPeriodId: workPeriod?.id },
      { enabled: scopeEnabled },
    );

  const rightScopeEnabled = isComparisonView && Boolean(rightScope?.team?.id || rightScope?.workflow?.id);
  const { taskTypes: rightEndpointTaskTypes, taskTypeOptions: rightEndpointTaskTypeOptions } =
    useProcessTaskTypesWithOptions(
      {
        teamId: rightScope?.team?.id,
        workflowId: rightScope?.workflow?.id,
        boardId: rightScope?.board?.id,
        workPeriodId: rightScope?.workPeriod?.id,
      },
      { enabled: rightScopeEnabled },
    );

  // Value→verbatim-label lookups for the Work Item Type dropdowns. Types
  // without a synced label (graph-hydrated types, pre-resync data) are absent
  // from the map and keep the legacy i18n/title-case rendering.
  const leftTaskTypeLabelsByValue = useMemo(
    () => buildTaskTypeLabelMap(leftEndpointTaskTypeOptions),
    [leftEndpointTaskTypeOptions],
  );
  const rightTaskTypeLabelsByValue = useMemo(
    () => buildTaskTypeLabelMap(rightEndpointTaskTypeOptions),
    [rightEndpointTaskTypeOptions],
  );

  // Refs to track the current project context to detect changes (team or workflow, but NOT board)
  const leftProjectContextRef = useRef<string>('');
  const rightProjectContextRef = useRef<string>('');

  // State for filter groups
  const [isLeftOpen, { toggle: toggleLeftOpen }] = useDisclosure(false);
  const [isRightOpen, { toggle: toggleRightOpen }] = useDisclosure(false);
  const [leftFilterGroups, setLeftFilterGroups] = useState<FilterGroup[]>([]);
  const [rightFilterGroups, setRightFilterGroups] = useState<FilterGroup[]>([]);
  const [viewMode, setViewMode] = useState<ProcessViewMode>(
    isComparisonView ? ProcessViewMode.COMPARISON : ProcessViewMode.SINGLE,
  );

  const handleToggleComparisonView = useCallback(() => {
    // First update view mode which is local state
    const newComparisonMode = !isComparisonView;
    setViewMode(newComparisonMode ? ProcessViewMode.COMPARISON : ProcessViewMode.SINGLE);

    // Then update the store
    setIsComparisonView(newComparisonMode);
  }, [isComparisonView, setIsComparisonView]);

  const { data: workflows } = useGetWorkflows(organizationId, portfolioId);

  // Get teams from global store
  const teams = useGlobalStore((state) => state.teams || []);

  // Set the default team when global teams change
  useDeepCompareEffect(() => {
    if (!teams || teams.length === 0) {
      return;
    }

    // Check if the currently selected team still exists in the new teams array
    const teamExists = team && teams.some((t) => t.id === team.id);
    const rightTeamExists = rightScope?.team && teams.some((t) => t.id === rightScope.team?.id);

    // Only set default team if current team doesn't exist or no team is selected
    if (!teamExists) {
      setScope({ team: teams[0] });
    }

    if (!rightTeamExists && rightScope) {
      setRightScope({ team: teams[0] });
    }
  }, [teams, team, rightScope]);

  // Reset left task types ref when project/team or workflow changes
  useEffect(() => {
    // Include workflow in the context key so switching workflows also clears stale task types
    const currentContext = `team-${team?.id || ''}-workflow-${workflow?.id || ''}`;

    // If the context has changed, reset the ref to only include base types
    // Don't add leftTaskTypes here as they might be stale from the previous team while the new fetch is in flight
    if (currentContext !== leftProjectContextRef.current) {
      leftProjectContextRef.current = currentContext;
      // Reset to base TaskType enum values and clear the endpoint flag so the
      // new scope gets a fresh evaluation from both sources.
      leftAllSeenTaskTypesRef.current = new Set(Object.values(TaskType));
      leftTypesFromEndpointRef.current = false;
      setLeftForceRender((prev) => prev + 1);
      // Return early - the effect will run again when leftTaskTypes updates with fresh data
      return;
    }

    // Context hasn't changed, just add any new task types if they appear (this handles fresh data updates).
    // Skip if the dedicated task-types endpoint has already replaced the ref with authoritative data.
    // Deduplicate case-insensitively so "Bug" and "bug" don't both appear in the dropdown.
    if (!leftTypesFromEndpointRef.current && leftTaskTypes?.length) {
      let updated = false;
      const seenLower = new Set(Array.from(leftAllSeenTaskTypesRef.current).map((t) => t.toLowerCase()));
      leftTaskTypes.forEach((type) => {
        const lowerType = type.toLowerCase();
        if (!seenLower.has(lowerType)) {
          leftAllSeenTaskTypesRef.current.add(type);
          seenLower.add(lowerType);
          updated = true;
        }
      });

      if (updated) {
        setLeftForceRender((prev) => prev + 1);
      }
    }
  }, [team?.id, workflow?.id, leftTaskTypes]);

  // Reset right task types ref when project/team or workflow changes
  useEffect(() => {
    if (!isComparisonView || !rightScope) {
      return;
    }

    const teamId = rightScope.team?.id || '';
    const workflowId = rightScope.workflow?.id || '';

    // Include workflow in the context key so switching workflows also clears stale task types
    const currentContext = `team-${teamId}-workflow-${workflowId}`;

    // If the context has changed, reset the ref to only include base types
    // Don't add rightTaskTypes here as they might be stale from the previous team while the new fetch is in flight
    if (currentContext !== rightProjectContextRef.current) {
      rightProjectContextRef.current = currentContext;
      rightAllSeenTaskTypesRef.current = new Set(Object.values(TaskType));
      rightTypesFromEndpointRef.current = false;
      setRightForceRender((prev) => prev + 1);
      // Return early - the effect will run again when rightTaskTypes updates with fresh data
      return;
    }

    // Context hasn't changed, just add any new task types if they appear (this handles fresh data updates).
    // Skip if the dedicated task-types endpoint has already replaced the ref with authoritative data.
    // Deduplicate case-insensitively so "Bug" and "bug" don't both appear in the dropdown.
    if (!rightTypesFromEndpointRef.current && rightTaskTypes?.length) {
      let updated = false;
      const seenLower = new Set(Array.from(rightAllSeenTaskTypesRef.current).map((t) => t.toLowerCase()));
      rightTaskTypes.forEach((type) => {
        const lowerType = type.toLowerCase();
        if (!seenLower.has(lowerType)) {
          rightAllSeenTaskTypesRef.current.add(type);
          seenLower.add(lowerType);
          updated = true;
        }
      });

      if (updated) {
        setRightForceRender((prev) => prev + 1);
      }
    }
  }, [isComparisonView, rightScope, rightTaskTypes]);

  // When the dedicated task-types endpoint returns a non-empty list, replace the
  // ref entirely so the dropdown shows exactly the types present in this scope.
  // Also reset leftTypesFromEndpointRef so the fallback hydration path (from the
  // process graph) is suppressed while endpoint data is available.
  useEffect(() => {
    if (leftEndpointTaskTypes && leftEndpointTaskTypes.length > 0) {
      leftAllSeenTaskTypesRef.current = new Set(leftEndpointTaskTypes);
      leftTypesFromEndpointRef.current = true;
      setLeftForceRender((prev) => prev + 1);
    } else {
      // Empty or undefined — fall back to base enum + graph hydration
      leftTypesFromEndpointRef.current = false;
    }
  }, [leftEndpointTaskTypes]);

  useEffect(() => {
    if (rightEndpointTaskTypes && rightEndpointTaskTypes.length > 0) {
      rightAllSeenTaskTypesRef.current = new Set(rightEndpointTaskTypes);
      rightTypesFromEndpointRef.current = true;
      setRightForceRender((prev) => prev + 1);
    } else {
      rightTypesFromEndpointRef.current = false;
    }
  }, [rightEndpointTaskTypes]);

  // Fetch epics for the left section
  const { epics: leftEpics = [] } = useEpics(
    {
      portfolioId: portfolio?.id || null,
      projectId: team?.id || null,
      subprojectId: board?.id || null,
    },
    {
      queryKey: ['epics', portfolio?.id, team?.id, board?.id],
      enabled: Boolean(team?.id),
      staleTime: 1000 * 60 * 5,
    },
  );

  // Fetch epics for the right section (only in comparison mode)
  const { epics: rightEpics = [] } = useEpics(
    {
      portfolioId: portfolio?.id || null,
      projectId: rightScope?.team?.id || null,
      subprojectId: rightScope?.board?.id || null,
    },
    {
      queryKey: ['epics-right', portfolio?.id, rightScope?.team?.id, rightScope?.board?.id],
      enabled: Boolean(rightScope?.team?.id) && isComparisonView,
      staleTime: 1000 * 60 * 5,
    },
  );

  // Fetch work periods for the left section
  const { data: leftWorkPeriods = [] } = useWorkPeriodsData(board?.id || null);

  // Fetch work periods for the right section (only in comparison mode)
  const { data: rightWorkPeriods = [] } = useWorkPeriodsData(rightScope?.board?.id || null);

  // Auto-select work period from URL params (set by Flora quick links)
  const [filterSearchParams, setFilterSearchParams] = useSearchParams();
  const didApplyWorkPeriodRef = useRef(false);
  useEffect(() => {
    if (didApplyWorkPeriodRef.current) return;
    const urlWorkPeriodIds = filterSearchParams.get('workPeriodIds');
    if (!urlWorkPeriodIds || leftWorkPeriods.length === 0) return;

    const ids = urlWorkPeriodIds.split(',').filter(Boolean);
    if (ids.length === 0) return;

    // Find the first matching work period (process module supports single selection)
    const wpToSelect = leftWorkPeriods.find((wp) => ids.includes(wp.id));
    if (!wpToSelect) return;

    setScope({ workPeriod: wpToSelect, taskTypes: null, epic: null, task: null });

    didApplyWorkPeriodRef.current = true;
    // Clear the workPeriodIds param now that it's been applied
    const remaining = new URLSearchParams(filterSearchParams);
    remaining.delete('workPeriodIds');
    setFilterSearchParams(remaining, { replace: true });
  }, [filterSearchParams, setFilterSearchParams, leftWorkPeriods, setScope]);

  // Update board when workflow changes for left side
  useEffect(() => {
    if (useWorkflow && workflow) {
      // If the current board is not in the workflow boards, clear it
      if (board && !workflow.subprojects.includes(board.id)) {
        setScope({ board: null });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setScope, useWorkflow, workflow, board]); // Intentionally omitting setScope to prevent loops

  // Update board when workflow changes for right side
  useEffect(() => {
    if (rightScope?.useWorkflow && rightScope.workflow) {
      // If the current board is not in the workflow boards, clear it
      if (rightScope.board && !rightScope.workflow.subprojects.includes(rightScope.board.id)) {
        setRightScope({ board: null });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setRightScope, rightScope?.useWorkflow, rightScope?.workflow, rightScope?.board]); // Intentionally omitting setRightScope to prevent loops

  // --------------------------------------------------
  // Left side handlers
  // --------------------------------------------------

  // Handle team selection/deselection
  const handleLeftTeamSelected = useCallback(
    (selectedTeam: any) => {
      setScope({ team: selectedTeam, board: null, epic: null, task: null });
    },
    [setScope],
  );

  // Handle board selection/deselection
  const handleLeftBoardSelected = useCallback(
    (selectedBoard: any) => {
      if (selectedBoard === null) {
        setScope({
          board: selectedBoard,
          epic: null,
          task: null,
          useGroups: true,
          workPeriod: null,
        });
      } else {
        setScope({ board: selectedBoard, epic: null, task: null, workPeriod: null });
      }
    },
    [setScope],
  );

  // Handle epic selection/deselection
  const handleLeftEpicSelected = useCallback(
    (selectedEpic: any) => {
      setScope({ epic: selectedEpic });
    },
    [setScope],
  );

  // Handle task selection/deselection
  const handleLeftTaskSelected = useCallback(
    (selectedTask: any) => {
      setScope({ task: selectedTask });
    },
    [setScope],
  );

  // Handle task type selection/deselection
  const handleLeftTaskTypeSelected = useCallback(
    (selectedTaskType: TaskTypeValue[] | null) => {
      setScope({ taskTypes: selectedTaskType });
    },
    [setScope],
  );

  // Handle workflow selection/deselection
  const handleLeftWorkflowChange = useCallback(
    (value: string | null) => {
      if (value) {
        const workflow = workflows?.find((w) => w.id === value);
        setScope({ workflow, board: null, task: null });
      } else {
        // Instead of allowing null, select the first workflow if available
        const firstWorkflow = workflows && workflows.length > 0 ? workflows[0] : null;
        if (firstWorkflow) {
          setScope({ workflow: firstWorkflow, board: null, task: null });
        }
      }
    },
    [workflows, setScope],
  );

  // Handle work period selection/deselection
  const handleLeftWorkPeriodSelected = useCallback(
    (selectedWorkPeriod: WorkPeriod | null) => {
      if (selectedWorkPeriod?.id === workPeriod?.id) {
        return;
      }
      if (selectedWorkPeriod) {
        setScope({
          workPeriod: selectedWorkPeriod,
        });
      } else {
        setScope({ workPeriod: null });
      }
    },
    [setScope, workPeriod],
  );

  // --------------------------------------------------
  // Right side handlers (for comparison view)
  // --------------------------------------------------

  // Handle team selection/deselection for right side
  const handleRightTeamSelected = useCallback(
    (selectedTeam: any) => {
      setRightScope({
        team: selectedTeam,
        board: null,
        epic: null,
        task: null,
      });
    },
    [setRightScope],
  );

  // Handle board selection/deselection for right side
  const handleRightBoardSelected = useCallback(
    (selectedBoard: any) => {
      if (selectedBoard === null) {
        setRightScope({
          board: selectedBoard,
          epic: null,
          task: null,
          useGroups: true,
          workPeriod: null,
        });
      } else {
        setRightScope({ board: selectedBoard, epic: null, task: null, workPeriod: null });
      }
    },
    [setRightScope],
  );

  // Handle epic selection/deselection for right side
  const handleRightEpicSelected = useCallback(
    (selectedEpic: any) => {
      setRightScope({ epic: selectedEpic });
    },
    [setRightScope],
  );

  // Handle task selection/deselection for right side
  const handleRightTaskSelected = useCallback(
    (selectedTask: any) => {
      setRightScope({ task: selectedTask });
    },
    [setRightScope],
  );

  // Handle task type selection/deselection for right side
  const handleRightTaskTypeSelected = useCallback(
    (selectedTaskType: TaskTypeValue[] | null) => {
      setRightScope({ taskTypes: selectedTaskType });
    },
    [setRightScope],
  );

  // Handle workflow selection/deselection for right side
  const handleRightWorkflowChange = useCallback(
    (value: string | null) => {
      if (value) {
        const workflow = workflows?.find((w) => w.id === value);
        setRightScope({ workflow, board: null, task: null });
      } else {
        // Instead of allowing null, select the first workflow if available
        const firstWorkflow = workflows && workflows.length > 0 ? workflows[0] : null;
        if (firstWorkflow) {
          setRightScope({ workflow: firstWorkflow, board: null, task: null });
        }
      }
    },
    [workflows, setRightScope],
  );

  // Handle work period selection/deselection for right side
  const handleRightWorkPeriodSelected = useCallback(
    (selectedWorkPeriod: WorkPeriod | null) => {
      if (selectedWorkPeriod?.id === rightScope?.workPeriod?.id) {
        return;
      }
      if (selectedWorkPeriod) {
        setRightScope({
          workPeriod: selectedWorkPeriod,
        });
      } else {
        setRightScope({ workPeriod: null });
      }
    },
    [setRightScope, rightScope?.workPeriod],
  );

  // Create handlers using helper functions
  const handleLeftWorkflowSelected = createWorkflowSelectHandler(setScope, workflows || [], teams);
  const handleRightWorkflowSelected = createWorkflowSelectHandler(setRightScope, workflows || [], teams);

  const handleLeftResetFilters = createResetFiltersHandler(setScope, useWorkflow, workflows || [], teams);

  const handleRightResetFilters = createResetFiltersHandler(
    setRightScope,
    rightScope?.useWorkflow || false,
    workflows || [],
    teams,
  );

  useDeepCompareEffect(() => {
    if (!useWorkflow && !team) {
      setScope({ team: teams?.[0] });
      return;
    }

    const groups = createProcessFilterGroups({
      teams: teams as any[],
      team: team as any,
      board: board as any,
      taskTypes: leftTaskTypes,
      selectedTaskTypes: taskTypes,
      epic,
      epics: leftEpics,
      useWorkflow,
      workflow,
      workflows: workflows || [],
      allSeenTaskTypesRef: leftAllSeenTaskTypesRef,
      taskTypeLabelsByValue: leftTaskTypeLabelsByValue,
      workPeriods: leftWorkPeriods,
      selectedWorkPeriod: workPeriod,
      onTeamSelected: handleLeftTeamSelected,
      onBoardSelected: handleLeftBoardSelected,
      onTaskTypeSelected: handleLeftTaskTypeSelected,
      onEpicSelected: handleLeftEpicSelected,
      onWorkflowChange: handleLeftWorkflowChange,
      onWorkPeriodSelected: handleLeftWorkPeriodSelected,
      t,
    });

    setLeftFilterGroups(groups);
  }, [
    teams,
    team,
    board,
    taskTypes,
    epic,
    useWorkflow,
    workflow,
    workflows,
    leftTaskTypes,
    leftTaskTypeLabelsByValue,
    leftForceRender,
    workPeriod,
    leftWorkPeriods,
    leftEpics,
    handleLeftTeamSelected,
    handleLeftBoardSelected,
    handleLeftTaskTypeSelected,
    handleLeftEpicSelected,
    handleLeftWorkflowChange,
    handleLeftTaskSelected,
    handleLeftWorkPeriodSelected,
    setScope,
    // Use i18n.language (stable string) instead of `t` — react-i18next's
    // returned `t` is referentially unstable across renders (see i18next/
    // react-i18next#1228), and useDeepCompareEffect can't deep-compare a
    // function body, so depending on `t` directly re-fires the effect on
    // every render. We only need to refire on actual language changes.
    i18n.language,
  ]);

  useDeepCompareEffect(() => {
    if (!isComparisonView || !rightScope) {
      return;
    }

    if (!rightScope.useWorkflow && !rightScope.team) {
      setRightScope({ team: teams?.[0] });
      return;
    }

    const groups = createProcessFilterGroups({
      teams: teams as any[],
      team: rightScope.team as any,
      board: rightScope.board as any,
      taskTypes: rightTaskTypes,
      selectedTaskTypes: rightScope.taskTypes,
      epic: rightScope.epic,
      epics: rightEpics,
      useWorkflow: rightScope.useWorkflow,
      workflow: rightScope.workflow,
      workflows: workflows || [],
      allSeenTaskTypesRef: rightAllSeenTaskTypesRef,
      taskTypeLabelsByValue: rightTaskTypeLabelsByValue,
      workPeriods: rightWorkPeriods,
      selectedWorkPeriod: rightScope.workPeriod,
      onTeamSelected: handleRightTeamSelected,
      onBoardSelected: handleRightBoardSelected,
      onTaskTypeSelected: handleRightTaskTypeSelected,
      onEpicSelected: handleRightEpicSelected,
      onWorkflowChange: handleRightWorkflowChange,
      onWorkPeriodSelected: handleRightWorkPeriodSelected,
      t,
    });

    setRightFilterGroups(groups);
  }, [
    isComparisonView,
    rightScope,
    teams,
    rightEpics,
    workflows,
    rightTaskTypes,
    rightTaskTypeLabelsByValue,
    rightForceRender,
    rightWorkPeriods,
    handleRightTeamSelected,
    handleRightBoardSelected,
    handleRightTaskTypeSelected,
    handleRightEpicSelected,
    handleRightWorkflowChange,
    handleRightWorkPeriodSelected,
    setRightScope,
    // See comment on the left-side effect's deps array — using i18n.language
    // (stable string) instead of `t` (unstable function reference).
    i18n.language,
  ]);

  const leftHeaderSection = (
    <MantineStack>
      <Switch
        label="Use Workflows"
        checked={useWorkflow}
        onChange={handleLeftWorkflowSelected}
        color={secondaryBase}
        disabled={!workflows || workflows.length === 0}
      />
    </MantineStack>
  );

  const rightHeaderSection = rightScope && (
    <MantineStack>
      <Switch
        label="Use Workflows"
        checked={rightScope.useWorkflow}
        onChange={handleRightWorkflowSelected}
        color={secondaryBase}
        disabled={!workflows || workflows.length === 0}
      />
    </MantineStack>
  );

  const viewOptions = [
    { value: ProcessViewMode.SINGLE, label: 'Single View' },
    { value: ProcessViewMode.COMPARISON, label: 'Comparison View' },
  ];

  return isComparisonView ? (
    <Stack gap="md">
      <Select value={viewMode} options={viewOptions} onChange={handleToggleComparisonView} />
      <Divider />
      <Group justify="space-between">
        <Text weight="bold">Left Graph</Text>
        <Icon name={isLeftOpen ? 'unfold_less' : 'unfold_more'} size={16} onClick={toggleLeftOpen} />
      </Group>
      <Collapse expanded={isLeftOpen}>
        <FilterPanel
          filterGroups={leftFilterGroups}
          onResetFilters={handleLeftResetFilters}
          useSelectFilters={true}
          headerSection={leftHeaderSection}
        />
        <TaskSelect
          handleTaskSelected={handleLeftTaskSelected}
          startDate={startDate}
          endDate={endDate}
          hideLabel={false}
        />
      </Collapse>
      <Divider />
      <Group justify="space-between">
        <Text weight="bold">Right Graph</Text>
        <Icon name={isRightOpen ? 'unfold_less' : 'unfold_more'} size={16} onClick={toggleRightOpen} />
      </Group>
      <Collapse expanded={isRightOpen}>
        <FilterPanel
          filterGroups={rightFilterGroups}
          onResetFilters={handleRightResetFilters}
          useSelectFilters={true}
          headerSection={rightHeaderSection}
        />
        <TaskSelect
          handleTaskSelected={handleRightTaskSelected}
          startDate={startDate}
          endDate={endDate}
          isRightSide={true}
          hideLabel={false}
        />
      </Collapse>
    </Stack>
  ) : (
    <>
      <FilterPanel
        options={viewOptions}
        selectedOption={viewMode}
        onOptionChange={handleToggleComparisonView}
        filterGroups={leftFilterGroups}
        onResetFilters={handleLeftResetFilters}
        useSelectFilters={true}
        headerSection={leftHeaderSection}
      />
      <TaskSelect handleTaskSelected={handleLeftTaskSelected} startDate={startDate} endDate={endDate} />
    </>
  );
}
